import React, { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import api from "../axiosSetup";
import moment from "moment";
import CustomNavbar from "../CustomNavbar";
import DataTable from "./DataTable";
import cfg from "../../config";

const ZRMoveBookings = () => {
  const location = useLocation();
  const { move } = location.state || {};

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [filterField, setFilterField] = useState("");
  const [totalSeats, setTotalSeats] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ show: false, bookingId: null, phase: null, text: "" });
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);

  const fetchBookings = async () => {
    if (!move) return;
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const res = await api.get(`/api/move-bookings/${move.id || move.moveID}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBookings(res.data || []);
      setError(null);
      if (res.data?.length) {
        setTotalSeats(res.data[0]?.totalSeats || null);
        const firstBool = res.data[0].fields.find((f) => f.dataType === "boolean");
        if (firstBool) setFilterField(firstBool.frontendName);
      }
    } catch (err) {
      setError(err.message || "Fehler beim Laden der Buchungen.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBookings(); }, [move]);

  const confirmBooking = async (bookingId, phase) => {
    try {
      setModalLoading(true);
      const token = localStorage.getItem("authToken");
      if (phase === "bus" || phase === "assembly") {
        await api.post("/api/movebookings/confirm", { bookingID: bookingId, phase }, { headers: { Authorization: `Bearer ${token}` } });
      } else if (phase === "moveFromWaitingList") {
        await api.post("/api/set-move-booking-to-bus", { bookingID: bookingId }, { headers: { Authorization: `Bearer ${token}` } });
      } else if (phase === "cancel") {
        await api.post("/api/delete-booking", { bookingID: bookingId }, { headers: { Authorization: `Bearer ${token}` } });
      }
      await fetchBookings();
      setConfirmModal({ show: false, bookingId: null, phase: null, text: "" });
    } catch (err) {
      setModalError(err.response?.data?.message || "Fehler bei der Bestätigung.");
    } finally {
      setModalLoading(false);
    }
  };

  // Flatten bookings for DataTable
  const flatBookings = useMemo(
    () =>
      bookings.map((b) => {
        const obj = { _id: b.id, _booking: b };
        (b.fields || []).forEach((f) => { obj[f.frontendName] = f; });
        return obj;
      }),
    [bookings]
  );

  // Derive columns from first booking's fields
  const columns = useMemo(() => {
    if (!bookings.length) return [];
    return bookings[0].fields.map((f) => ({
      key: f.frontendName,
      label: f.frontendName,
      render: (fieldObj) => {
        if (!fieldObj) return <span className="dt-muted">—</span>;
        if (fieldObj.dataType === "boolean") {
          return fieldObj.value
            ? <span className="dt-badge dt-badge-yes">✓</span>
            : <span className="dt-badge dt-badge-no">✗</span>;
        }
        if (fieldObj.dataType === "datetime") {
          return moment(fieldObj.value).isValid()
            ? moment(fieldObj.value).format("DD.MM.YYYY HH:mm") + " Uhr"
            : fieldObj.value ?? <span className="dt-muted">—</span>;
        }
        if (fieldObj.value == null) return <span className="dt-muted">—</span>;
        return String(fieldObj.value);
      },
      searchValue: (fieldObj) => {
        if (!fieldObj) return "";
        const v = fieldObj.value;
        if (v == null) return "";
        if (fieldObj.dataType === "boolean") return v ? "ja" : "nein";
        if (fieldObj.dataType === "datetime") {
          return moment(v).isValid() ? moment(v).format("DD.MM.YYYY HH:mm") + " Uhr" : String(v);
        }
        return String(v);
      },
    }));
  }, [bookings]);

  // Filter by status
  const filteredFlat = useMemo(() => {
    if (statusFilter === "all" || !filterField) return flatBookings;
    return flatBookings.filter((row) => {
      const fieldObj = row[filterField];
      if (!fieldObj || fieldObj.dataType !== "boolean") return true;
      if (statusFilter === "confirmed") return fieldObj.value === true;
      if (statusFilter === "notConfirmed") return fieldObj.value === false;
      return true;
    });
  }, [flatBookings, statusFilter, filterField]);

  const occupiedSeats = bookings.filter(
    (b) => b.fields.find((f) => f.key === "isWaitingList")?.value === false
  ).length;

  const boolFields = bookings[0]?.fields.filter((f) => f.dataType === "boolean") || [];

  const actions = (row) => {
    const isWaiting = row._booking.fields.find((f) => f.key === "isWaitingList")?.value === true;
    const busConf = row._booking.fields.find((f) => f.key === "presentBusToMove")?.value === true;
    const setupConf = row._booking.fields.find((f) => f.key === "presentAtMove")?.value === true;
    return (
      <div className="dt-actions">
        {!isWaiting && !busConf && (
          <button className="dt-btn dt-btn-primary"
            onClick={() => setConfirmModal({ show: true, bookingId: row._id, phase: "bus", text: "Bestätigen, dass die Person mit dem Bus mitgefahren ist?" })}>
            Bus bestätigen
          </button>
        )}
        {busConf && !setupConf && (
          <button className="dt-btn dt-btn-warning"
            onClick={() => setConfirmModal({ show: true, bookingId: row._id, phase: "assembly", text: "Bestätigen, dass die Person am Aufstellungsplatz anwesend war?" })}>
            Aufstellung bestätigen
          </button>
        )}
        {isWaiting && (
          <button className="dt-btn dt-btn-success"
            onClick={() => setConfirmModal({ show: true, bookingId: row._id, phase: "moveFromWaitingList", text: "Person von der Warteliste auf einen Busplatz verschieben?" })}>
            Busplatz zuweisen
          </button>
        )}
        <button className="dt-btn dt-btn-danger"
          onClick={() => setConfirmModal({ show: true, bookingId: row._id, phase: "cancel", text: "Buchung wirklich stornieren?" })}>
          Stornieren
        </button>
      </div>
    );
  };

  if (!move) return <div className="container mt-4">Nichts ausgewählt.</div>;

  const cityField = move.fields?.find((f) => f.key === "city");

  return (
    <>
      <CustomNavbar />
      <div className="container-fluid px-3 px-md-4 mt-4">
        <h2 className="mb-3">
          Buchungen: {cityField?.value || cfg.eventTerm}
        </h2>

        {/* Stats */}
        {totalSeats !== null && (
          <div className="d-flex flex-wrap gap-2 mb-3">
            <span className="dt-badge dt-badge-info" style={{ fontSize: 14, padding: "6px 14px" }}>
              🚌 Max. Busplätze: {totalSeats}
            </span>
            <span className="dt-badge dt-badge-yes" style={{ fontSize: 14, padding: "6px 14px" }}>
              ✓ Belegt: {occupiedSeats}
            </span>
            <span className="dt-badge dt-badge-neutral" style={{ fontSize: 14, padding: "6px 14px" }}>
              Frei: {Math.max(0, totalSeats - occupiedSeats)}
            </span>
          </div>
        )}

        {/* Status filter */}
        <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
          <span style={{ fontSize: 13, color: "#6c757d", fontWeight: 500 }}>Status-Filter:</span>
          <select className="dt-select" value={filterField} onChange={(e) => setFilterField(e.target.value)}>
            {boolFields.map((f) => (
              <option key={f.key} value={f.frontendName}>{f.frontendName}</option>
            ))}
          </select>
          <select className="dt-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">Alle</option>
            <option value="confirmed">✓ Bestätigt</option>
            <option value="notConfirmed">✗ Nicht bestätigt</option>
          </select>
        </div>

        <DataTable
          data={filteredFlat}
          columns={columns}
          loading={loading}
          error={error}
          actions={actions}
          searchPlaceholder="Buchung suchen…"
          emptyText="Keine Buchungen gefunden."
          initialPageSize={25}
          pageSizeOptions={[10, 25, 50, 100]}
        />
        <br />
      </div>

      {/* Bestätigungsmodal */}
      {confirmModal.show && (
        <div className="modal-overlay" onClick={() => !modalLoading && setConfirmModal({ show: false })}>
          <div className="modal-box animate-in" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title text-dark">Bestätigung</h5>
              <button className="modal-close" onClick={() => setConfirmModal({ show: false })}>&times;</button>
            </div>
            <div className="modal-body text-dark">
              <p>{confirmModal.text}</p>
              {modalError && <div className="alert alert-danger mt-2">{modalError}</div>}
            </div>
            <div className="modal-footer mt-3 d-flex gap-2 justify-content-end">
              <button className="btn btn-secondary" disabled={modalLoading} onClick={() => setConfirmModal({ show: false })}>
                Abbrechen
              </button>
              <button className="btn btn-success" disabled={modalLoading}
                onClick={() => confirmBooking(confirmModal.bookingId, confirmModal.phase)}>
                {modalLoading ? "Wird gespeichert…" : "Ja, bestätigen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ZRMoveBookings;
